import { useState } from "react";
import { Breadcrumb } from "react-bootstrap";
import { NavLink, useHistory, useParams } from "react-router-dom";
import usersApi, { UserUpdateReq, useUserById } from "../../api/users";
import { Loader, toast, toastError } from "../../common/components/core";
import { User } from "../../types/users";
import { PageTitle } from "../../types/pageTitle";
import UpdateUserForm, {
    UpdateUserFormValues,
} from "./components/UpdateUserForm";
import { GenericError, composeErrorHandlers, onStaleStateError } from "../../utils/errorHandling";
import { useGeneralErrorHandlerCreator } from "../../common/hooks/useGeneralErrorHandler";
import { useAuthErrorHandlerCreator } from "../../common/hooks";

const UpdateUserView = (): JSX.Element => {
    document.title = PageTitle.UpdateUserView;
    const { userId } = useParams<{ userId: string }>();
    const { data: userData, isValidating: userIsValidating } =
        useUserById(userId);
    const history = useHistory();
    const onAuthError = useAuthErrorHandlerCreator();
    const useGeneralError = useGeneralErrorHandlerCreator();
    const [isSubmitting, setSubmitting] = useState(false);
    const submit = async (values: UpdateUserFormValues, userId: string) => {
        if (values) {
            setSubmitting(true);
            const payload: UserUpdateReq = {
                firstName: values.firstName,
                lastName: values.lastName,
                role: values.role,
                userProfileId: +values.userProfileId,
            };
            try {
                await usersApi.update(payload, userId);
                toast("User Updated");
                history.push(`/user-management/user-information/${userId}`);
            } catch (e) {
                console.log(e)
                composeErrorHandlers(
                    onStaleStateError(() => {
                        toastError("Matching project exists for the original role.");
                    }),
                    onAuthError,
                    useGeneralError,
                )(e as GenericError);
            }
            setSubmitting(false);
        }
    };

    return (
        <PureUpdateUserView
            isLoading={userIsValidating || isSubmitting}
            userData={userData}
            onSubmit={submit}
        />
    );
};

type PureUpdateUserViewProps = {
    isLoading: boolean;
    userData: User | undefined;
    onSubmit: (values: UpdateUserFormValues, userId: string) => void;
};

export const PureUpdateUserView = ({
    isLoading,
    userData,
    onSubmit,
}: PureUpdateUserViewProps): JSX.Element => {
    const history = useHistory();

    return (
        <Loader isLoading={isLoading} bgClassName="bg-body">
            {userData && (
                <>
                    <Breadcrumb className="mb-5">
                        <Breadcrumb.Item linkAs="span">
                            <NavLink to="/user-management">
                                User Management
                            </NavLink>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item active className="fw-bold">
                            Edit User Information
                        </Breadcrumb.Item>
                    </Breadcrumb>
                    <UpdateUserForm
                        onCancel={() => history.push("/user-management")}
                        onSubmit={(values: UpdateUserFormValues) => {
                            onSubmit(values, userData.id);
                        }}
                        user={userData}
                    />
                </>
            )}
        </Loader>


    );
};

export default UpdateUserView;
